import { FC, useState, MouseEvent, useEffect } from 'react';
import { Box } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import MainLayout from 'src/pages/private';
import SearchBar from 'src/components/InputFields/Searchbar';
import VoucherGrid from 'src/components/Grids/VoucherGrid';
import FilterPopover from 'src/components/StyledComponents/FilterPopover';
import CreatedDatePicker from 'src/components/InputFields/DatePicker';
import FormikSelectField, {
  Option,
} from 'src/components/InputFields/FormikSelectField';
import { voucherFilterInitialValues } from 'src/utils/initialValues.utils';
import { voucherFilterValidation } from 'src/utils/validationScheme.utils';
import { voucherStatusOptions, apiUrls } from 'src/utils/properties.utils';
import { useGetBasicVoucherTypes } from 'src/services/voucherType.service';
import {
  VoucherFilterValues,
  VoucherViewData,
} from 'src/models/Management.interface';
import VoucherFormDialog from 'src/components/DialogBox/VoucherFormDialog/VoucherFormDialog';
import VoucherViewDialog from 'src/components/DialogBox/VoucherViewDialog/VoucherViewDialog';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  PrimaryActionButton,
  ButtonIcon,
  FilterButton,
  FilterIcon,
  FilterPopoverIcon,
  FilterPopoverTitle,
  FilterPopoverDivider,
  FilterCloseIcon,
  FilterLabel,
  SmallActionButton,
  SpacedContainer,
  ErrorHelperTextContainer,
} from 'src/components/StyledComponents/StyledPageComponents';

const VoucherManagement: FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSnackbar } = StyledSnackbar();

  const [voucherAdded, setVoucherAdded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedVoucher, setSelectedVoucher] =
    useState<VoucherViewData | null>(null);
  const [filterValues, setFilterValues] = useState<VoucherFilterValues>({
    voucherTypeId: '',
    status: '',
    startDate: null,
    endDate: null,
  });

  const { basicVoucherTypes, fetchBasicVoucherTypes } =
    useGetBasicVoucherTypes();

  // Handle view parameter from URL to open VoucherViewDialog
  useEffect(() => {
    const viewVoucherId = searchParams.get('view');
    if (viewVoucherId) {
      const fetchAndOpenViewDialog = async () => {
        try {
          const response = await axiosTokenInstance.get(
            apiUrls.GET_VOUCHER_BY_ID,
            {
              params: {
                id: viewVoucherId,
              },
            }
          );
          setSelectedVoucher(response.data);
          setViewDialogOpen(true);
        } catch (error) {
          const errorMessage = await getApiErrorMessage(error);
          showSnackbar(errorMessage, 'error');
          // Remove the view parameter if voucher fetch fails
          searchParams.delete('view');
          setSearchParams(searchParams);
        }
      };
      fetchAndOpenViewDialog();
    }
  }, [searchParams]);

  const voucherTypeOptions: Option[] = basicVoucherTypes.map((vt) => ({
    label: vt.name,
    value: vt.id,
  }));

  const statusOptions: Option[] = voucherStatusOptions.map((opt) => ({
    label: opt.label,
    value: opt.value,
  }));

  const handleSearchChange = (newQuery: string) => {
    setSearchTerm(newQuery);
  };

  const handleClickOpen = () => {
    setAddDialogOpen(true);
  };

  const handleFilterClick = (event: MouseEvent<HTMLButtonElement>) => {
    fetchBasicVoucherTypes();
    setAnchorEl(event.currentTarget);
    setFilterOpen(true);
  };

  const handleClose = () => {
    filterFormik.resetForm();
    setAnchorEl(null);
    setFilterOpen(false);
  };

  const handleClear = () => {
    filterFormik.resetForm();
    setFilterValues({
      voucherTypeId: '',
      status: '',
      startDate: null,
      endDate: null,
    });
  };

  const filterFormik = useFormik({
    initialValues: voucherFilterInitialValues,
    validationSchema: voucherFilterValidation,
    onSubmit: (values) => {
      setFilterValues({
        voucherTypeId: values.voucherTypePicker,
        status: values.statusPicker,
        startDate: values.startDate,
        endDate: values.endDate,
      });
      setFilterOpen(false);
    },
  });

  const handleAddSuccess = () => {
    setAddDialogOpen(false);
    setVoucherAdded(!voucherAdded);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedVoucher(null);
    // Remove the view parameter from URL
    searchParams.delete('view');
    setSearchParams(searchParams);
  };

  const handleVoucherUpdated = () => {
    setVoucherAdded((prev) => !prev);
  };

  const SearchBarComponent = () => (
    <SearchBar
      id="search"
      value={searchTerm}
      onChange={handleSearchChange}
      placeholder="Search by Payee or Bill Number"
      width="280px"
    />
  );

  const PrimaryActionButtonComponent = () => (
    <PrimaryActionButton
      className="btn-primary"
      width="160px"
      onClick={handleClickOpen}
    >
      <ButtonIcon component="i" className="bx bx-plus 1" />
      New Voucher
    </PrimaryActionButton>
  );

  return (
    <MainLayout
      title="Voucher Management"
      searchBar={<SearchBarComponent />}
      button={<PrimaryActionButtonComponent />}
      filterButton={
        <FilterButton
          className="btn-filter"
          width="100px"
          onClick={handleFilterClick}
        >
          <FilterIcon component="i" className="bx bx-filter" />
          Add Filter
        </FilterButton>
      }
    >
      <FilterPopover
        open={filterOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
      >
        <Box>
          <Box display="flex" alignItems="center">
            <FilterPopoverIcon component="i" className="bx bx-filter" />
            <FilterPopoverTitle>Filter Data By</FilterPopoverTitle>
          </Box>
          <FilterPopoverDivider />
          <FilterCloseIcon
            component="i"
            className="bx bx-x-circle"
            onClick={handleClose}
          />
        </Box>

        <Box display="flex" justifyContent="space-between" mb={1}>
          <FormikSelectField
            id="voucherTypePicker"
            label="Voucher Type"
            height="28px"
            width="150px"
            customMessage="Voucher Type"
            textColor="var(--subtext)"
            borderColor="var(--hover-white)"
            fontSize="10px"
            fontWeight="400"
            showLabel={false}
            formikProps={filterFormik}
            options={voucherTypeOptions}
          />
          <FormikSelectField
            id="statusPicker"
            label="Status"
            height="28px"
            width="150px"
            customMessage="Status"
            textColor="var(--subtext)"
            borderColor="var(--hover-white)"
            fontSize="10px"
            fontWeight="400"
            showLabel={false}
            formikProps={filterFormik}
            options={statusOptions}
          />
        </Box>

        <Box>
          <FilterLabel>Created at:</FilterLabel>

          <SpacedContainer display="flex" justifyContent="space-between">
            <CreatedDatePicker
              name="startDate"
              placeholder="Start Date"
              formikProps={filterFormik}
              fontSize="10px"
            />

            <CreatedDatePicker
              name="endDate"
              placeholder="End Date"
              formikProps={filterFormik}
              fontSize="10px"
            />
          </SpacedContainer>
          {filterFormik.touched.endDate && filterFormik.errors.endDate && (
            <ErrorHelperTextContainer>
              {filterFormik.errors.endDate}
            </ErrorHelperTextContainer>
          )}
        </Box>

        <Box mt={4} display="flex" justifyContent="space-between">
          <SmallActionButton
            btnVariant="inactive"
            width="70px"
            mr={2}
            onClick={handleClear}
          >
            Clear
          </SmallActionButton>

          <SmallActionButton
            className="btn-primary"
            width="70px"
            onClick={() => filterFormik.handleSubmit()}
          >
            Apply
          </SmallActionButton>
        </Box>
      </FilterPopover>

      <VoucherGrid
        state={voucherAdded}
        searchTerm={searchTerm}
        filters={filterValues}
      />

      <VoucherFormDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={handleAddSuccess}
        mode="add"
      />

      <VoucherViewDialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        data={selectedVoucher}
        onApprove={handleVoucherUpdated}
        onReject={handleVoucherUpdated}
        onDelete={handleCloseViewDialog}
        fetchVouchers={handleVoucherUpdated}
      />
    </MainLayout>
  );
};

export default VoucherManagement;
